import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Globe, ExternalLink, HandHeart } from "lucide-react";
import { HiredSignalBox } from "./HiredSignalBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExternalWebJobsCard = () => {
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/jobs/external`)
      .then((res) => setJobs(res.data || []))
      .catch(() => setJobs([]));
  }, []);

  if (jobs.length === 0) {
    return (
      <Card className="mt-6 border-sky-200" data-testid="external-jobs-section">
        <CardContent className="pt-4 pb-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-600">
            🤝 <strong>Viu uma vaga em algum grupo?</strong> Divulgue grátis no Balcão público — ajuda outros sangradores.
          </p>
          <a
            href="/balcao"
            target="_blank"
            rel="noreferrer"
            data-testid="external-jobs-indicate-btn"
            className="shrink-0 px-4 py-2 rounded-lg bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700"
          >
            Divulgar vaga →
          </a>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6 border-sky-200" data-testid="external-jobs-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Globe className="w-4 h-4 text-sky-600" />
          Vagas encontradas na web
          <Badge variant="outline" className="text-[10px]">via Radar 🛰️</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Vagas de sites externos. Confirme as informações diretamente na fonte antes de se candidatar.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {jobs.map((job) => (
            <div key={job.id} className="flex items-center justify-between gap-2 border rounded-lg p-3" data-testid={`external-job-${job.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {job.urgent && <span className="text-red-600 font-bold" data-testid={`ext-urgent-${job.id}`}>🔥 URGENTE · </span>}
                  {job.title}
                </p>
                {job.confirmed_at && (
                  <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold" data-testid={`ext-confirmed-${job.id}`}>
                    ✅ Vaga confirmada em {new Date(job.confirmed_at).toLocaleDateString("pt-BR")}
                  </span>
                )}
                {job.summary && <p className="text-xs text-gray-600">{job.summary}</p>}
                <p className="text-[11px] text-muted-foreground">
                  {job.city ? `${job.city} · ` : ""}
                  {job.submitter_name ? `🤝 Indicada por ${job.submitter_name}` : `Fonte: ${job.source_name || "externa"}`}
                </p>
                {job.contact && (
                  <p className="text-xs font-semibold text-sky-800" data-testid={`external-job-contact-${job.id}`}>
                    📱 {job.contact}
                  </p>
                )}
                <HiredSignalBox jobId={job.id} compact />
              </div>
              {job.source_url && (
                <a
                  href={job.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:underline"
                  data-testid={`external-job-link-${job.id}`}
                >
                  Ver vaga <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500">🤝 Viu uma vaga em algum grupo? Divulgue grátis.</p>
          <a
            href="/balcao"
            target="_blank"
            rel="noreferrer"
            data-testid="external-jobs-indicate-link"
            className="text-xs font-semibold text-sky-700 hover:underline"
          >
            Divulgar vaga no Balcão →
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export const AvailableLaborCard = () => {
  const [laborOffers, setLaborOffers] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/labor-offers`)
      .then((res) => setLaborOffers(res.data || []))
      .catch(() => setLaborOffers([]));
  }, []);

  if (laborOffers.length === 0) return null;

  return (
    <Card className="mt-6 border-amber-200" data-testid="labor-offers-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HandHeart className="w-4 h-4 text-amber-600" />
          Profissionais disponíveis
          <Badge variant="outline" className="text-[10px]">via Balcão 🤝</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Pessoas que se ofereceram para trabalhar. Entre em contato direto e confirme as informações.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {laborOffers.map((offer) => (
            <div key={offer.id} className="border rounded-lg p-3" data-testid={`labor-offer-${offer.id}`}>
              <p className="text-sm font-medium">{offer.title}</p>
              {offer.confirmed_at && (
                <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-semibold" data-testid={`labor-confirmed-${offer.id}`}>
                  ✅ Disponibilidade confirmada em {new Date(offer.confirmed_at).toLocaleDateString("pt-BR")}
                </span>
              )}
              {offer.summary && <p className="text-xs text-gray-600">{offer.summary}</p>}
              {offer.confirm_details && (offer.confirm_details.plantas || offer.confirm_details.facas) && (
                <p className="text-[11px] text-gray-600" data-testid={`labor-details-${offer.id}`}>
                  {offer.confirm_details.plantas ? `🌱 ${offer.confirm_details.plantas} plantas` : ""}
                  {offer.confirm_details.plantas && offer.confirm_details.facas ? " · " : ""}
                  {offer.confirm_details.facas ? `🔪 ${offer.confirm_details.facas}` : ""}
                </p>
              )}
              {offer.reference?.name && (
                <p className="text-[11px] text-emerald-700 font-medium" data-testid={`labor-reference-${offer.id}`}>
                  ⭐ Referência: {offer.reference.name}{offer.reference.farm ? ` — ${offer.reference.farm}` : ""}
                </p>
              )}
              <p className="text-[11px] text-muted-foreground">
                {offer.city ? `${offer.city} · ` : ""}
                {offer.submitter_name ? `${offer.submitter_name}` : ""}
              </p>
              <a
                href={`/oferta/${offer.id}`}
                className="inline-block mt-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
                data-testid={`labor-offer-contact-${offer.id}`}
              >
                🔓 Ver contato →
              </a>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
